import React, { useEffect, useState } from "react";
import ClientSidebarLinks from "./ClientSidebarLinks";
import { useAuth } from "../../context/AuthContext";

const ClientDashboard: React.FC = () => {
  const { user } = useAuth();
  const [projects, setProjects] = useState<any[]>([]);
  const [team, setTeam] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchUserProjects = async () => {
      if (!user?.id) return;
      try {
        const res = await fetch(
          `${API_BASE}/get_user_projects.php?user_id=${user.id}`
        );
        const data = await res.json();
        setProjects(data);
      } catch (err) {
        console.error("Error fetching user projects:", err);
      } finally {
        setLoading(false);
      }
    };

    const fetchProjectTeam = async () => {
      if (!user?.id) return;
      try {
        const res = await fetch(
          `${API_BASE}/get_project_team.php?user_id=${user.id}`
        );
        const data = await res.json();
        setTeam(data);
      } catch (err) {
        console.error("Error fetching project team:", err);
      }
    };

    fetchUserProjects();
    fetchProjectTeam();
  }, [user]);

  const completedCount = projects.filter((p) => p.status === "completed").length;
  const inProgressCount = projects.filter((p) => p.status === "in-progress" || !p.status || p.status === 'active').length;

  return (
    <>
      <style>{`
        /* ... styles truncated for brevity in artifact, keeping existing ones ... */
        * {
          box-sizing: border-box;
          font-family: Arial, sans-serif;
        }

        body {
          margin: 0;
        }

        .dashboard {
          display: flex;
          height: 100vh;
          background: #f6f8fb;
        }

        .sidebar {
          width: 220px;
          background: #ffffff;
          padding: 20px;
          border-right: 1px solid #e5e7eb;
        }

        .primary-btn {
          width: 100%;
          padding: 10px;
          background: #22c55e;
          border: none;
          color: white;
          border-radius: 6px;
          margin-bottom: 20px;
          cursor: pointer;
        }

        .sidebar nav a {
          display: block;
          padding: 10px;
          color: #555;
          text-decoration: none;
          border-radius: 6px;
          margin-bottom: 6px;
          cursor: pointer;
        }

        .sidebar nav a.active,
        .sidebar nav a:hover {
          background: #eafaf0;
          color: #22c55e;
        }

        .content {
          flex: 1;
          padding: 24px;
          overflow-y: auto;
        }

        .stats {
          display: grid;
          grid-template-columns: repeat(3, 1fr);
          gap: 16px;
          margin-bottom: 24px;
        }

        .stat-card {
          background: white;
          padding: 20px;
          border-radius: 10px;
          text-align: center;
        }

        .stat-value {
          font-size: 28px;
          margin-bottom: 6px;
          font-weight: bold;
        }

        .green { color: #22c55e; }
        .orange { color: #f59e0b; }
        .blue { color: #3b82f6; }

        .card {
          background: white;
          border-radius: 10px;
          padding: 20px;
          margin-bottom: 24px;
        }

        .team-grid {
          display: grid;
          grid-template-columns: repeat(3, 1fr);
          gap: 16px;
        }

        .team-card {
          display: flex;
          align-items: center;
          gap: 12px;
          padding: 12px;
          border: 1px solid #eee;
          border-radius: 8px;
        }

        .avatar {
          width: 36px;
          height: 36px;
          background: #22c55e;
          color: white;
          display: flex;
          align-items: center;
          justify-content: center;
          border-radius: 50%;
          font-weight: bold;
        }

        .tag {
          margin-left: auto;
          background: #eef2ff;
          padding: 4px 8px;
          border-radius: 6px;
          font-size: 12px;
        }

        .activity-list li {
          display: flex;
          justify-content: space-between;
          padding: 10px 0;
          border-bottom: 1px solid #eee;
        }

        .status span {
          font-size: 12px;
          text-transform: capitalize;
        }

        .done { color: #22c55e; }
        .in-progress { color: #f59e0b; }
        .in-review { color: #8b5cf6; }
        .todo { color: #3b82f6; }

        .floating-actions {
          position: fixed;
          right: 20px;
          bottom: 20px;
          display: flex;
          flex-direction: column;
          gap: 10px;
        }

        .fab {
          width: 44px;
          height: 44px;
          border-radius: 50%;
          border: none;
          color: white;
          cursor: pointer;
          font-size: 18px;
        }

        .fab.green { background: #22c55e; }
        .fab.blue { background: #3b82f6; }
        .fab.red { background: #ef4444; }
      `}</style>

      <div className="dashboard">
        {/* Sidebar */}
        <aside className="sidebar">
          <h2 className="text-xl font-bold text-green-600 mb-4">{user?.name}</h2>
          <button className="primary-btn">Contact Team</button>
          <nav>
            <ClientSidebarLinks active="myproject" />
          </nav>
        </aside>

        {/* Main Content */}
        <main className="content">
          <div className="mb-3">
            <span className="text-xs font-bold uppercase tracking-wider text-green-600 bg-green-50 border border-green-200 px-3 py-1 rounded-full">
              {user?.role === 'employee' || user?.role === 'scrum_master' ? 'Employee Dashboard' : 'Client Dashboard'}
            </span>
          </div>
          <h1 className="text-2xl font-semibold mb-1">Welcome, {user?.name}</h1>
          <p className="text-gray-500 mb-6 font-medium">
            Here's the status of your assigned projects
          </p>

          {/* Stats */}
          <div className="stats">
            <div className="stat-card">
              <div className="stat-value gray">{projects.length}</div>
              <p>Total Projects</p>
            </div>
            <div className="stat-card">
              <div className="stat-value green">{completedCount}</div>
              <p>Completed</p>
            </div>
            <div className="stat-card">
              <div className="stat-value orange">{inProgressCount}</div>
              <p>In Progress</p>
            </div>
          </div>

          <div className="card">
            <h3>Your Assigned Projects</h3>
            {loading ? (
              <p className="text-gray-500">Loading projects...</p>
            ) : projects.length === 0 ? (
              <p className="text-gray-500">No project assigned yet.</p>
            ) : (
              <div className="space-y-4 pt-2">
                {projects.map((p) => (
                  <div key={p.id} className="p-4 border rounded-lg flex justify-between items-center">
                    <div>
                      <h4 className="font-bold text-brand-secondary">{p.name}</h4>
                      <p className="text-sm text-gray-500">{p.description}</p>
                    </div>
                    <span className="text-xs bg-green-100 text-green-700 font-bold px-3 py-1 rounded-full uppercase">
                      {p.status || "active"}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Progress */}
          <div className="card">
            <h3>Project Progress</h3>
            <div className="w-full h-3 bg-gray-200 rounded-full mt-2">
              <div
                className="h-3 bg-green-500 rounded-full transition-all duration-500"
                style={{
                  width:
                    projects.length > 0
                      ? `${(completedCount / projects.length) * 100}%`
                      : "0%",
                }}
              />
            </div>
          </div>

          {/* Team */}
          <div className="card">
            <h3>Your Project Team</h3>
            <div className="team-grid">
              {team.length === 0 ? (
                <p className="text-gray-500">No team members assigned.</p>
              ) : (
                team.map((member, i) => (
                  <div className="team-card" key={i}>
                    <div className="avatar">{member.name ? member.name[0] : "?"}</div>
                    <div>
                      <strong>{member.name}</strong>
                      <p>{member.role}</p>
                    </div>
                    <span className="tag">{member.role}</span>
                  </div>
                ))
              )}
            </div>
          </div>
        </main>

        {/* Floating Buttons */}
        <div className="floating-actions">
          <button className="fab green">+</button>
          <button className="fab blue">📞</button>
          <button className="fab red">✉</button>
        </div>
      </div>
    </>
  );
};

const API_BASE = import.meta.env.VITE_API_BASE_URL || '/api';

export default ClientDashboard;
